import { loadCartPna } from "../../cart/cart/pna/cartPna";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  type ConfiguredQuickEntryItem,
  type QuickEntryForm,
  type QuickEntryItem,
  type QuickEntryItemPC,
} from "../types/models";
import { useOnMount } from "~/common/hooks/useOnMount";
import { type SrpProductData } from "~/utils/models";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { type ProductData } from "~/domains/product/types/ProductData";
import { UndoButton } from "~/common/components/Button/UndoButton";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import {
  AutosuggestProductInput,
  isSameProduct,
  useFetchProductAutosuggest,
} from "~/common/components/AutosuggestProductInput/AutosuggestProductInput";
import { hasProductConfigured } from "~/features/quickEntry/utils/helpers";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import Parameter from "~/common/components/Parameter";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { Alert } from "~/common/mui/Alert/Alert";
import { IconButton, Stack, Typography } from "~/common/mui";
import Delete from "~/common/mui/icons/Delete";

interface RowProps extends DivAttributes {
  index: number;
  validateRow?: (row: ConfiguredQuickEntryItem, itemsPopup?: QuickEntryItemPC[]) => ReactNode[];
  item: QuickEntryItem;
  items?: QuickEntryItemPC[];
  onRemove: () => void;

  variant: "pc" | "wishlist" | "b2c-cart";
  rightSideColumns?: readonly string[];
}

export const QuickEntryTableRow = ({
  index,
  item,
  onRemove,
  validateRow,
  variant,
  items,
  rightSideColumns,
  ...rest
}: RowProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext<QuickEntryForm>();
  const [isUpdatedWithSuccessor, setUpdatedWithSuccessor] = useState(false);
  const [successorProduct, setSuccessorProduct] = useState<ProductData | null>(null);

  const formNamePrefix = `items.${index}` as const;

  function getFieldName<T extends keyof QuickEntryItem>(name: T) {
    return `items.${index}.${name}` as const;
  }

  const getError = (name: keyof QuickEntryItem) => errors.items?.[index]?.[name] ?? undefined;

  const fromSuccessorClick = useRef(false);

  useOnMount(() => {
    register(getFieldName("product"));
    register(getFieldName("productId"));
    register(getFieldName("previousProductId"));
  });

  const { product, productId, previousProductId } = item;

  const productCode = product?.code;
  // Basic data arrives with the autosuggest selection; PnA loads through the canonical
  // queries (fire-and-forget like the legacy thunk dispatch).
  useEffect(() => {
    if (productCode) {
      void loadCartPna({ productCode, priceReference: "" }).catch(() => undefined);
    }
  }, [productCode]);

  const qtyError = getError("quantity");
  const productError = getError("product");

  const { inputProps, products, selectedProduct } = useFetchProductAutosuggest(productId ?? "");

  const refreshProductError = (product?: SrpProductData) => {
    if (product) {
      clearErrors(formNamePrefix);
    } else {
      setError(getFieldName("product"), { type: "required" });
    }
  };

  useEffect(() => {
    let productToUpdate: SrpProductData = selectedProduct as SrpProductData;
    if (
      selectedProduct &&
      isProductPMA(fakeDescriptorFromProduct(selectedProduct)) &&
      selectedProduct.successorProduct
    ) {
      productToUpdate = selectedProduct.successorProduct as SrpProductData;
      setUpdatedWithSuccessor(true);
      setSuccessorProduct(productToUpdate);
      setValue(getFieldName("productId"), productToUpdate!.designation);
    } else if (!successorProduct || successorProduct.designation !== selectedProduct?.designation) {
      setUpdatedWithSuccessor(false);
      setSuccessorProduct(null);
    }

    if (fromSuccessorClick.current) {
      fromSuccessorClick.current = false;
    } else {
      setValue(getFieldName("previousProductId"), "");
    }

    setValue(getFieldName("product"), productToUpdate);

    if (productError) {
      refreshProductError(productToUpdate);
    }
  }, [selectedProduct?.code]);

  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const isPhone = !isMinTablet;

  let validationResult: ReactNode[] = [];
  if (hasProductConfigured(item) && validateRow) {
    validationResult = validateRow(item, items);
  }

  const quantityVisible = () => {
    if (!product) {
      return true;
    }
    return product ? product.quantityVisible : false;
  };

  const showQuantity = ["wishlist", "b2c-cart"].includes(variant);

  return (
    <Stack gap={1}>
      <Stack gap={2} direction="row" alignItems="center">
        <AutosuggestProductInput
          {...inputProps}
          label={t("bulkUpload.table.header.productId")}
          inputValue={productId ?? ""}
          onInputChange={(event, value, reason) => {
            if (reason !== "reset") {
              setValue(getFieldName("productId"), value);
            }
          }}
          onChange={(event, value) => {
            if (!isSameProduct(value ?? "", selectedProduct)) {
              setValue(getFieldName("productId"), value ?? "");
            }
            refreshProductError(selectedProduct);
          }}
          onBlur={() => {
            refreshProductError(selectedProduct);
          }}
          products={products}
          disableNewVerLink={true}
          fullWidth
          size="small"
          error={validationResult.length > 0 || !!productError || !!qtyError}
        />
        {showQuantity && (
          <Controller
            render={({ field: { value, onChange, onBlur } }) => (
              <>
                <CustomNumberFormat
                  size="small"
                  hidden={!quantityVisible()}
                  data-test-id={"quick-entry-item-quantity"}
                  label={t("cartPage.table.header.quantity.title")}
                  handleState={(state) => onChange(state?.floatValue ?? null)}
                  error={!!qtyError}
                  value={value as number | null}
                  onBlur={onBlur}
                />
                {!quantityVisible() && <Parameter label={t("input.quantity")} value={item.quantity} />}
              </>
            )}
            name={getFieldName("quantity")}
            control={control}
            defaultValue={item.quantity}
            rules={VALIDATE_QTY}
          />
        )}
        <IconButton color="primary" onClick={onRemove} size="small" sx={{ marginRight: "-4px" }}>
          <Delete color="primary" />
        </IconButton>
      </Stack>
      <Stack gap={1}>
        {productError && (
          <Typography pl={1.5} color="error" variant="bodySmall">
            {t("bulkUpload.error.product.id.invalid")}
          </Typography>
        )}
        {qtyError && (
          <Typography pl={1.5} color="error" variant="bodySmall">
            {t("bulkUpload.error.product.quantity.invalid")}
          </Typography>
        )}
        {validationResult.map((node) => node)}
        {product && product.isPMA && <InfoProductMessagePMA />}
        {product && NewVersionMessage.isVisible(product) && (
          <NewVersionMessage
            baseProduct={product}
            disableLink={true}
            onClick={() => {
              fromSuccessorClick.current = true;
              setValue(getFieldName("previousProductId"), productId);
              setValue(getFieldName("productId"), product.successorProduct!.designation);
            }}
          />
        )}
        {isUpdatedWithSuccessor && <Alert severity={"info"}>{t("quickEntry.updatedWithSuccessor")}</Alert>}
        {previousProductId && (
          <UndoButton
            onClick={() => {
              setValue(getFieldName("previousProductId"), "");
              setValue(getFieldName("productId"), previousProductId);
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

const VALIDATE_QTY = {
  validate: (value: number) => !!value && value >= 1,
};
